import Spinner from 'Components/Spinner/Spinner';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { GoUnlock } from 'react-icons/go';

import { EnableButton, MerchantsDataTableContainer, MessageWrapper } from '../styles';
import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { MerchantsQueryEdges } from './types';

// createTheme creates a new theme named solarized that overrides the build in dark theme
createTheme('polarized', TableThemeSettings);

interface Props {
  merchantsList?: Array<MerchantsQueryEdges[number]>;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  limit: number;
  isErrorRequest?: boolean;
  enablePayouts: (merchantId: string) => void;
}

function MerchantsDataTable({
  merchantsList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  isErrorRequest,
  enablePayouts,
}: Props) {
  const EnableIcon = GoUnlock as FunctionComponent;

  const columns: TableColumn<MerchantsQueryEdges[number]>[] = useMemo(
    () => [
      {
        name: 'Merchant ID',
        selector: (row: MerchantsQueryEdges[number]) => row.node.merchantId,
        width: '200px',
      },
      {
        name: 'Business name',
        selector: (row: MerchantsQueryEdges[number]) => row.node.businessProfile.businessName,
        minWidth: '150px',
      },
      {
        name: 'Brand name',
        selector: (row: MerchantsQueryEdges[number]) => {
          return <div>{`${row.node.businessProfile.brandName}`}</div>;
        },
        width: '220px',
      },
      {
        name: 'Trading country',
        selector: (row: MerchantsQueryEdges[number]) => {
          return <div>{`${row.node.businessProfile.tradingCountry}`}</div>;
        },
        width: '220px',
      },
      {
        cell: (row: MerchantsQueryEdges[number]) => (
          <div className="hover-button-wrapper">
            <EnableButton
              className="hover-button"
              title="Enable Payouts"
              onClick={() => enablePayouts(row.node.merchantId)}
            >
              <EnableIcon />
            </EnableButton>
          </div>
        ),
        width: '50px',
      },
    ],
    [EnableIcon, enablePayouts],
  );

  return (
    <MerchantsDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        data={merchantsList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="40px" width="40px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationPerPage={limit}
        sortServer
        persistTableHead
        defaultSortFieldId={4}
        defaultSortAsc={false}
        paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
      />
    </MerchantsDataTableContainer>
  );
}

export default MerchantsDataTable;
